import { BillingCycle } from '@up2stay/up2promo-api-client';
import { cn } from '@up2stay/up2promo-ui';

import { YEARLY_FREE_MONTHS } from '@/core/billing/plan-catalog';
import { useDictionary } from '@/core/providers/dictionary-provider';

export interface BillingCycleToggleProps {
  billingCycle: BillingCycle;
  onBillingCycleChange: (cycle: BillingCycle) => void;
  className?: string;
}

/**
 * Segmented Monthly / Yearly control matching approved billing comps.
 * Yearly save badge stays on one line (DE/EN) — track grows with content.
 */
export function BillingCycleToggle({
  billingCycle,
  onBillingCycleChange,
  className,
}: BillingCycleToggleProps) {
  const planDict = useDictionary().workspace.create.planSelection;
  const plansPage = useDictionary().workspace.billing.plansPage;
  const isYearly = billingCycle === BillingCycle.YEARLY;
  const months = String(YEARLY_FREE_MONTHS);
  const saveLabel = plansPage.yearlySaveBadge.replace('{months}', months);

  const segmentClass = (active: boolean) =>
    cn(
      'inline-flex h-[38px] cursor-pointer items-center justify-center gap-1.5',
      'rounded-[9px] border-0 text-sm transition-colors duration-150',
      'whitespace-nowrap focus-visible:outline-none focus-visible:ring-2',
      'focus-visible:ring-ring focus-visible:ring-offset-2',
      active
        ? 'bg-background font-semibold text-foreground shadow-sm dark:bg-muted'
        : 'bg-transparent font-medium text-muted-foreground hover:text-foreground',
    );

  return (
    <div
      className={cn(
        'inline-flex w-full min-w-0 shrink-0 rounded-xl bg-muted/70 p-1 sm:w-auto',
        'dark:bg-muted/40',
        className,
      )}
      role="group"
      aria-label={planDict.billingCycleLabel}
    >
      <button
        type="button"
        aria-pressed={!isYearly}
        onClick={() => onBillingCycleChange(BillingCycle.MONTHLY)}
        className={cn(
          segmentClass(!isYearly),
          'min-w-0 flex-1 px-4 sm:flex-none sm:px-[18px]',
        )}
      >
        {planDict.monthly}
      </button>
      <button
        type="button"
        aria-pressed={isYearly}
        onClick={() => onBillingCycleChange(BillingCycle.YEARLY)}
        className={cn(
          segmentClass(isYearly),
          'shrink-0 px-3 pl-3.5 sm:px-3 sm:pl-[18px]',
        )}
      >
        {planDict.yearly}
        <span
          className={cn(
            'shrink-0 whitespace-nowrap rounded-md px-1.5 py-0.5',
            'text-[10px] font-semibold leading-none',
            'bg-emerald-100 text-emerald-800',
            'dark:bg-emerald-950 dark:text-emerald-300',
          )}
        >
          {saveLabel}
        </span>
      </button>
    </div>
  );
}
